import { Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardCard from '/@/components/bayer/dashboard-card';
import { useGrowersDashboard } from '/@/hooks/dashboards';
import { type parseUserNode, useAdvisorRvLinks, useAncestors, useUserNodes } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { getNodeScopeFilters } from '/@/utils/node-scope';
import { reverse } from '/@/utils/router';
import { ProfileIcon } from '../profile-icon';

const RVCard = ({ userNode }: { userNode: ReturnType<typeof parseUserNode> }) => {
    const navigate = useNavigate();
    const growersByRvQuery = useGrowersDashboard({ node: userNode.id, ...getNodeScopeFilters('rvs') });
    const consultantsQuery = useAncestors(
        { node: userNode?.id, types: ['consultant'] },
        { enabled: Boolean(userNode?.id) }
    );
    const advisorRvLinksQuery = useAdvisorRvLinks();
    const advisors = useMemo(() => {
        const advisors = advisorRvLinksQuery.data?.results?.filter((link) => link.rv.id === userNode?.id) || [];
        return advisors.map((link) => link.advisor);
    }, [advisorRvLinksQuery.data, userNode?.id]);

    const team = useMemo(() => {
        return [...(consultantsQuery.data || []), ...advisors];
    }, [consultantsQuery.data, advisors]);

    return (
        <Stack
            className='mp-mask'
            p={2}
            spacing={1.5}
            sx={{
                borderRadius: '8px',
                border: (t) => `1px solid ${t.palette.divider}`,
                background: '#E7F8FF',
            }}
        >
            <Stack justifyContent='space-between' direction='row' alignItems='center'>
                <Stack spacing={1}>
                    <Stack direction='row' spacing={1}>
                        <ProfileIcon userNode={userNode} type='primary' />
                        <Stack>
                            <Typography variant='subtitle1' fontWeight='bold'>
                                {userNode.name}
                            </Typography>
                            <Typography variant='body2' color='primary.mui.inactive'>
                                {userNode.email}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack spacing={1} direction='row' alignItems='center'>
                        <Typography variant='body2' color='primary.mui.inactive'>
                            {_('Equipo de trabajo: ')}
                        </Typography>
                        <Stack spacing={-0.5} direction='row'>
                            {team?.map((userNode, index) => (
                                <ProfileIcon
                                    key={userNode.id}
                                    userNode={userNode}
                                    type={userNode.type as 'consultant' | 'advisor'}
                                    size={26}
                                    sx={{
                                        position: 'relative',
                                        zIndex: team.length - index,
                                    }}
                                />
                            ))}
                        </Stack>
                    </Stack>
                </Stack>
                <Typography variant='h4' color='primary'>
                    {growersByRvQuery.data?.count}
                </Typography>
            </Stack>

            <Typography
                color='secondary'
                variant='subtitle1'
                sx={{ textDecoration: 'underline', cursor: 'pointer', width: 'fit-content' }}
                onClick={() => {
                    navigate(
                        reverse('RVs.detail', {
                            params: {
                                rvId: userNode.id,
                            },
                        })
                    );
                }}
            >
                {_('Ver detalle')}
            </Typography>
        </Stack>
    );
};

const GrowersByRVSection = ({ userNodeId }: { userNodeId?: number }) => {
    const rvsQuery = useUserNodes({ node: userNodeId, type: 'rv', page_size: 10000 });
    return (
        <DashboardCard
            title={_('Productores por RV')}
            subtitle={_('Productores agregados por RV y su equipo de trabajo')}
        >
            <Stack spacing={1} height='100%'>
                {rvsQuery.data?.results?.map((rv) => (
                    <RVCard key={rv.id} userNode={rv} />
                ))}
            </Stack>
        </DashboardCard>
    );
};

export default GrowersByRVSection;
